import { Hotel } from "./hotel";
import { Request } from "./request";
import { ServiceCategory } from "./serviceCategory";
import { Team } from "@/services/teamService";
import { ServicePartner } from "./servicePartner";
import { PartnerHotelOutOfCatalogSummary } from "./partner-order";

export enum HotelServiceCategory {
  HOTEL = "HOTEL",
  RESTAURANT = "RESTAURANT",
  LAUNDRY = "LAUNDRY",
  MINIBAR = "MINIBAR",
}

export enum HotelServiceType {
  TOUR = "TOUR",
  THEATER = "THEATER",
  EXCURSION = "EXCURSION",
  HORSEBACK = "HORSEBACK",
  EVENT = "EVENT",
  GUIDE = "GUIDE",
}

export enum HotelServiceStatus {
  ACTIVE = "ACTIVE",
  INACTIVE = "INACTIVE",
}

export enum HotelServiceAutoCreateTrigger {
  BEFORE_CHECKIN = "BEFORE_CHECKIN",
  DAILY_IN_HOUSE = "DAILY_IN_HOUSE",
  AFTER_DEPARTURE = "AFTER_DEPARTURE",
}

export enum HotelServiceProviderType {
  GUEST_SERVICE = "INTERNAL",
  EXTERNAL_SERVICE = "EXTERNAL",
  GUESTERIA_PARTNERS_SERVICE = "PARTNERS",
}

export enum WeekDay {
  Mon = "Mon",
  Tue = "Tue",
  Wed = "Wed",
  Thu = "Thu",
  Fri = "Fri",
  Sat = "Sat",
  Sun = "Sun",
}

export interface HotelServiceImportResult {
  total: number;
  imported: number;
  failed: number;
  errors: Array<{
    row: number;
    name?: string;
    error: string;
  }>;
}

export const WeekDayNum = {
  [WeekDay.Sun]: 0,
  [WeekDay.Mon]: 1,
  [WeekDay.Tue]: 2,
  [WeekDay.Wed]: 3,
  [WeekDay.Thu]: 4,
  [WeekDay.Fri]: 5,
  [WeekDay.Sat]: 6,
};

export interface HotelServiceContractor {
  name: string;
  surname: string;
  phone: string;
  email: string;
  fee: number;
}

export interface ServiceDisplayStageSettings {
  checkinCheckout: boolean;
  dashboardCarousel: boolean;
  dashboardCtaList: boolean;
  dashboardRecommendedServices: boolean;
  chatDisplay: boolean;
}

export interface ServiceDisplaySettings {
  beforeCheckin: ServiceDisplayStageSettings;
  inHouse: ServiceDisplayStageSettings;
  beforeCheckout: ServiceDisplayStageSettings;
}

export interface HotelServiceAssignedStaff {
  id: string;
  profile?: {
    firstName?: string | null;
    lastName?: string | null;
    photoId?: string | null;
  } | null;
}

export interface HotelService {
  id: string;
  photoId: string;
  name: string;
  description: string;
  price?: string;
  currency?: string;
  discount?: string;
  hotelId: string;
  category: ServiceCategory;
  type: HotelServiceType;
  status: HotelServiceStatus;
  providerType: HotelServiceProviderType;
  isRecommended: boolean;
  isAutomaticallyCreated?: boolean;
  autoCreateTriggers?: HotelServiceAutoCreateTrigger[];
  availableFromTime: string | null;
  availableToTime: string | null;
  availableDays: WeekDay[];
  checkinAvailable: boolean;
  checkoutAvailable: boolean;
  displaySettings?: ServiceDisplaySettings;
  contractor?: HotelServiceContractor;
  staffId?: string | null;
  teamId?: string | null;
  staff?: HotelServiceAssignedStaff | null;
  team?: Team | null;
  createdAt: Date;
  updatedAt: Date;
  hotel: Hotel;
  ServiceRequest: Request[];
  partner?: ServicePartner;
}

export interface CreateHotelServiceRequest {
  name: string;
  photoId?: string;
  description: string;
  price?: number;
  discount?: number;
  providerType: HotelServiceProviderType;
  categoryId: string;
  amenityId?: string;
  category?: HotelServiceCategory;
  status: HotelServiceStatus;
  type?: HotelServiceType;
  isRecommended: boolean;
  isAutomaticallyCreated?: boolean;
  autoCreateTriggers?: HotelServiceAutoCreateTrigger[];
  checkinAvailable: boolean;
  checkoutAvailable: boolean;
  availableFromTime: string;
  availableToTime: string;
  availableDays: WeekDay[];
  staffId?: string | null;
  teamId?: string | null;
  hotelId: string;
  contractor?: HotelServiceContractor;
  displaySettings?: ServiceDisplaySettings;
}

export interface UpdateHotelServiceRequest {
  name?: string;
  photoId?: string;
  description?: string;
  price?: number | null;
  discount?: number | null;
  providerType?: HotelServiceProviderType;
  category?: HotelServiceCategory;
  status?: HotelServiceStatus;
  type?: HotelServiceType;
  isRecommended?: boolean;
  isAutomaticallyCreated?: boolean;
  autoCreateTriggers?: HotelServiceAutoCreateTrigger[];
  checkinAvailable?: boolean;
  checkoutAvailable?: boolean;
  availableFromTime?: string;
  availableToTime?: string;
  availableDays?: WeekDay[];
  contractor?: Partial<HotelServiceContractor>;
  displaySettings?: ServiceDisplaySettings;

  staffId?: string | null;
  teamId?: string | null;
}

export interface PriceRange {
  min: number;
  max: number;
}

export interface HotelServicesResponse {
  data: HotelService[];
  total: number;
  page: number;
  limit: number;
  priceRange?: PriceRange;
}

/**
 * Response from GET /hotels/:hotelId/partners/orders/top-revenue.
 * `serviceId` is null and `serviceName` is the partner product name when the
 * top order was booked outside the catalog (contract confirmed 2026-09-27).
 */
export interface TopServiceByRevenue {
  serviceId: string | null;
  serviceName: string;
  totalRevenue: number;
  outOfCatalog: boolean;
}

/** Response from GET /hotels/:hotelId/partners/orders/top-booked */
export interface TopServiceByBookings {
  serviceId: string | null;
  serviceName: string;
  totalBookings: number;
  outOfCatalog: boolean;
}

/**
 * Stripped hotel summary from GET /hotels/:hotelId/partners/orders/summary.
 * `totalBooked`/`earnings` — renamed from `totalCost`/`totalProfit` (tasks
 * 0028/0029, contract 2026-09-28): "Earnings" is what the hotel/tenant UI
 * shows, never a rate or a Guesteria-side amount.
 */
export interface HotelPartnerOrdersSummary {
  totalOrders: number;
  /** Booked: guest price of the orders, not income (DV-68). */
  totalRevenue: number;
  /** Partner revenue: commission, earned + expected. Show this as income. */
  partnerRevenue: number;
  totalBooked: number;
  earnings: number;
  currency: string;
  cancelledOrders: number;
  /** Count of REFUNDED orders (DV-72); absent on older backends. */
  refundedOrders?: number;
  refundedAmount: number;
  /** Orders whose amounts are estimates, not exact partner figures (task 0031, contract 2026-09-29). */
  estimatedOrders: number;
  /** Not yet earned — the "Expected" tile (task 0033, contract 2026-09-29). */
  pendingEarnings: number;
  pendingOrders: number;
  /**
   * Bookings made outside this hotel's configured catalog. No commission at
   * hotel scope (contract confirmed 2026-09-27) — that only shows at
   * tenant/admin/total-revenue level.
   */
  outOfCatalog: PartnerHotelOutOfCatalogSummary;
}

export interface HotelServicesFilters {
  price?: string;
  providerType?: string;
  type?: string;
  status?: string;
  categoryId?: string;
  tagIds?: number[];
  staffIds?: string[];
  teamIds?: string[];
  sort?: string;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  page?: number;
  limit?: number;
  hotelId?: string;
}

export interface PartnerServiceCategoryFilterOption {
  /** First tag of the category group. */
  tagId: number;
  name: string;
  /** Every tag of the group (DV-159); the filter sends them all. */
  tagIds?: number[];
}

export interface PartnerServiceFilterOptions {
  categories: PartnerServiceCategoryFilterOption[];
  priceRange: PriceRange | null;
}
